// Port of api/fuel/app/classes/insights/worth/overview.php

import Segment from 'offline/insights/segment'
import Constants from 'offline/constants'
import Util from 'offline/util'

function parseGamesIndex(value) {
	if (!value) return []
	try {
		var parsed = JSON.parse(value)
		if (Array.isArray(parsed)) return parsed.map(function(id) { return parseInt(id, 10) }).filter(Boolean)
		if (parsed && Array.isArray(parsed.games)) return parsed.games.map(function(id) { return parseInt(id, 10) }).filter(Boolean)
		if (parsed && Array.isArray(parsed.index)) return parsed.index.map(function(id) { return parseInt(id, 10) }).filter(Boolean)
	} catch (e) {
		// Fall through to legacy comma-separated parsing.
	}
	return String(value).split(',').map(function(id) { return parseInt(id, 10) }).filter(Boolean)
}

function uniqueInts(values) {
	var out = []
	var seen = {}
	for (var i = 0; i < values.length; i++) {
		var value = parseInt(values[i], 10)
		if (!value || seen[value]) continue
		seen[value] = true
		out.push(value)
	}
	return out
}

export default Segment.extend({
	name: 'overview',
	types: [Constants.TYPE_USER],
	insight: false,
	detail: false,
	run: function(type, owner) {
		var overview = {
			percentValued: 0,
			avgGame: 0,
			avgExpansion: 0,
			increased: 0,
			decreased: 0,
			avgDiff: 0,
			avgPlay: 0,
			avgPlayedPlay: 0,
			over50: 0,
			over100: 0
		}

		var insightRows = Util.queryFast(
			'SELECT * FROM insight_user_collection WHERE user_id = ' + owner.get_id() + ' ORDER BY insight_date DESC LIMIT 1'
		)
		var insight = insightRows.length ? insightRows[0] : null
		var collectionIndex = parseGamesIndex(insight ? insight.insight_games : null)

		if (!collectionIndex.length) return { overview: overview }

		var valuedRows = Util.queryFast(
			'SELECT game_id FROM insight_game_value WHERE game_id IN(' + Util.in(collectionIndex) + ')' +
			' AND insight_value > 0 AND insight_stamp = "' + insight.insight_stamp + '"'
		)
		var valuedIndex = uniqueInts(valuedRows.map(function(row) { return row.game_id }))
		var expansionRows = valuedIndex.length ? Util.queryFast(
			'SELECT game_id FROM game WHERE game_id IN(' + Util.in(valuedIndex) + ')' +
			' AND game_type = ' + Constants.GAME_TYPE_EXPANSION
		) : []
		var expansionIndex = uniqueInts(expansionRows.map(function(row) { return row.game_id }))
		var expansionSet = {}
		for (var e = 0; e < expansionIndex.length; e++) expansionSet[expansionIndex[e]] = true
		var gameIndex = []
		for (var g = 0; g < valuedIndex.length; g++) if (!expansionSet[valuedIndex[g]]) gameIndex.push(valuedIndex[g])

		if (gameIndex.length) {
			var sumPlaysResult = Util.queryFast(
				'SELECT SUM(collection_plays) AS sum FROM collection WHERE user_id = ' + owner.get_id() +
				' AND game_id IN(' + Util.in(gameIndex) + ')'
			)
			var sumPlays = parseInt(sumPlaysResult[0].sum, 10) || 0
			if (sumPlays) overview.avgPlay = Math.round((parseFloat(insight.insight_value) || 0) / sumPlays)
		}

		var insightCount = parseInt(insight.insight_count, 10) || 0
		if (valuedIndex.length && insightCount) overview.percentValued = Math.round((100 / insightCount) * valuedIndex.length)
		if (gameIndex.length) overview.avgGame = Math.round((parseFloat(insight.insight_value_base) || 0) / gameIndex.length)
		if (expansionIndex.length) overview.avgExpansion = Math.round((parseFloat(insight.insight_value_expansions) || 0) / expansionIndex.length)

		if (valuedIndex.length) {
			// Combined over-N counts: one query, one SUM CASE per threshold,
			// one row returned. Was two separate COUNT(*) queries.
			var stampLiteral = '"' + String(insight.insight_stamp).replace(/"/g, '""') + '"'
			var overRows = Util.queryFast(
				'SELECT' +
				' SUM(CASE WHEN insight_value >= 50 THEN 1 ELSE 0 END) AS o50,' +
				' SUM(CASE WHEN insight_value >= 100 THEN 1 ELSE 0 END) AS o100' +
				' FROM insight_game_value WHERE game_id IN(' + Util.in(valuedIndex) + ')' +
				' AND insight_stamp = ' + stampLiteral
			)
			overview.over50 = (overRows[0] && overRows[0].o50) | 0
			overview.over100 = (overRows[0] && overRows[0].o100) | 0

			// Restored to the original cross-join approach: SQLite handles
			// the join + ORDER efficiently for ~500K rows; the cost was
			// previously dominated by JS-side parseInt/parseFloat. With
			// queryFast + integer coercion + a tight first/last walk
			// (skip the per-game array allocation) the JS pass is fast.
			var gameValuesRaw = Util.queryFast(
				'SELECT c.game_id AS g, v.insight_value AS v' +
				' FROM collection c, insight_game_value v' +
				' WHERE c.user_id = ' + owner.get_id() +
				' AND c.game_id = v.game_id' +
				' AND v.insight_date > c.collection_date_owned' +
				' AND c.game_id IN(' + Util.in(valuedIndex) + ')' +
				' AND v.insight_value > 0' +
				' ORDER BY c.game_id ASC, v.insight_date ASC'
			)

			// Walk the rows once. Because they're ordered by (game_id,
			// insight_date), we can compute first/last per game inline:
			// the first row for a new game is `first`, every subsequent
			// row updates `last`, and we close the group when game_id
			// changes. No per-game array allocation, no JSON object map.
			var totalChange = 0
			var changeCount = 0
			var prevGame = 0
			var firstV = 0
			var lastV = 0
			var rowCountForGame = 0
			var rowsLen = gameValuesRaw.length
			function flushGame() {
				if (rowCountForGame > 1 && firstV) {
					var change = lastV - firstV
					var percent = (100 / firstV) * (change < 0 ? -change : change)
					if (percent >= 10) {
						if (change > 0) overview.increased++
						else if (change < 0) overview.decreased++
					}
					totalChange += change
					changeCount++
				}
			}
			for (var i = 0; i < rowsLen; i++) {
				var row = gameValuesRaw[i]
				var g = row.g | 0
				var v = +row.v || 0
				if (g !== prevGame) {
					if (prevGame) flushGame()
					prevGame = g
					firstV = v
					lastV = v
					rowCountForGame = 1
				} else {
					lastV = v
					rowCountForGame++
				}
			}
			if (prevGame) flushGame()

			if (changeCount) {
				overview.avgDiff = Math.round((totalChange / changeCount) * 100) / 100
				if (overview.avgDiff > 10) overview.avgDiff = Math.round(overview.avgDiff)
			}
		}

		return {
			overview: overview
		}
	}
})
